import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Observable } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { IFileUploader, UploadFileResult, UploadProgress } from './file-uploader.service';

@Component({
  selector: 'sabis-ngx-filepicker',
  templateUrl: './file-picker.component.html',
  styleUrls: ['./file-picker.component.scss'],
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatProgressBarModule,
    MatButtonModule
  ]
})
export class SabisNgxFilePickerComponent implements OnInit, OnChanges {
  /** initial files received from developer */
  @Input('files') inputFiles: FileUploaderItem<any>[] = [];
  @Input('isEditable') isEditable: boolean = true;
  @Input('isMultiple') isMultiple: boolean = true;
  @Input('required') required: boolean = false;
  @Input('label') label: string = 'Select file';
  @Input('directoryPath') directoryPath: string = '';
  @Output('fileChanged') fileChanged: EventEmitter<FileChangedEvent> = new EventEmitter();
  @Input('onBeforeFileChange') onBeforeFileChange: ((args: FileChangedEvent[]) => Observable<boolean>) | null = null;
  @Input('uploader') fileUploader!: IFileUploader;
  /** Accepted file types (e.g., 'image/*', '.pdf')
   * If set, only files matching these types will be accepted. Multiple types can be specified, separated by commas.
   * Check: https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input/file#unique_file_type_specifiers
   */
  @Input('acceptFileTypes') accept: string = '';
  /** Max file size in bytes. If set, files exceeding this size will be rejected. */
  @Input('maxFileSize') maxFileSize: number | null = null;
  @Output('fileValidationError') fileValidationError: EventEmitter<FileValidationError> = new EventEmitter();

  public selectedFiles: File[] | File | null = null;
  public files: FileItem[] = [];

  /** internal id counter */
  private _id: number = 0;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputFiles']) {
      this.files = [];
      this.clearFileInput();

      if (!this.inputFiles) {
        return;
      }

      this.inputFiles.forEach(nf => {
        this.files.push({
          id: nf.id,
          serverId: null,
          downloadUrl: nf.downloadUrl,
          fileName: nf.fileName,
          fileSize: nf.fileSize,
          isWaiting: false,
          isUploading: false,
          selectedFile: null,
          abortController: null,
          uploadedPercentage: 100
        });
      });
    }
  }

  ngOnInit(): void {
  }

  /**
   * file input selected files changed
   * @param fileEvent
   */
  public onFileChanged(fileEvent: FileList | File): void {
    // clear array (only one file is allowed)
    if (!this.isMultiple) {
      this.files = [];
    }

    let newFiles: File[] = [];
    if (fileEvent instanceof FileList) {
      // fill newFiles from FileList
      for (let i = 0; i < fileEvent.length; i++) {
        newFiles.push(fileEvent.item(i)!);
      }
    }
    else {
      // fill newFiles from File
      newFiles.push(fileEvent);
    }

    newFiles.forEach(f => {
      if (!this.validateFile(f)) {
        return;
      }

      this._id = this._id - 1;

      let file: FileItem = {
        id: this._id,
        serverId: null,
        fileName: f.name,
        fileSize: f.size,
        isUploading: true,
        uploadedPercentage: 0,
        isWaiting: false,
        selectedFile: f,
        downloadUrl: '',
        abortController: new AbortController()
      };
      this.files.push(file);

      // create a controller and get its signal
      const abortSignal = file.abortController!.signal;

      let directoryPath: string = this.directoryPath;
      this.fileUploader.uploadFile(f, directoryPath, f.name, abortSignal,
        ((p: UploadProgress) => {
          file.uploadedPercentage = parseFloat(p.uploadedPercentage.toFixed(2));
        }),
        ((result: UploadFileResult) => {
          file.isUploading = false;
          file.uploadedPercentage = 100;
          file.downloadUrl = '';
          file.serverId = result.serverId;

          this.fileChanged.emit({
            id: file.id,
            fileName: file.fileName!,
            fileSize: file.fileSize!,
            isRemoved: false,
            filePath: result.filePath,
            uploadResult: result
          });
        })
      );
    });

    this.clearFileInput();
  }

  public onCancelUpload(file: FileItem): void {
    file.abortController!.abort();
    file.isUploading = false;
    file.uploadedPercentage = 0;
    this.removeFile(file);
  }

  public onRemoveFile(file: FileItem): void {
    this.removeFile(file);
    this.clearFileInput();
  }

  public isUploading(): boolean {
    return this.files.some(f => f.isUploading);
  }

  private removeFile(file: FileItem): void {
    this.files = this.files.filter(f => f.id !== file.id);
    //this.selectedFile = null;
    file.fileName = '';
    file.fileSize = 0;

    this.fileChanged.emit({
      id: file.id,
      fileName: '',
      fileSize: 0,
      isRemoved: true,
      filePath: '',
      uploadResult: null
    });
  }

  private validateFile(file: File): boolean {
    if (this.maxFileSize != null && file.size > this.maxFileSize) {
      this.fileValidationError.emit({
        fileName: file.name,
        fileSize: file.size,
        error: 'maxFileSize',
        message: `File "${file.name}" exceeds the maximum allowed size of ${this.maxFileSize / 1024 / 1024} MB.`
      });
      return false;
    }

    if (this.accept && !this.isFileTypeAccepted(file)) {
      this.fileValidationError.emit({
        fileName: file.name,
        fileSize: file.size,
        error: 'fileType',
        message: `File "${file.name}" does not match the accepted file types (${this.accept}).`
      });
      return false;
    }

    return true;
  }

  private isFileTypeAccepted(file: File): boolean {
    const acceptedTypes = this.accept.split(',').map(t => t.trim().toLowerCase());
    const fileName = file.name.toLowerCase();
    const fileExtension = '.' + fileName.split('.').pop();
    const mimeType = file.type.toLowerCase();

    return acceptedTypes.some(accepted => {
      if (accepted.startsWith('.')) {
        // Extension match (e.g. ".pdf", ".jpg")
        return fileExtension === accepted;
      } else if (accepted.endsWith('/*')) {
        // Wildcard MIME type (e.g. "image/*")
        const baseType = accepted.replace('/*', '');
        return mimeType.startsWith(baseType + '/');
      } else {
        // Exact MIME type match (e.g. "application/pdf")
        return mimeType === accepted;
      }
    });
  }

  private clearFileInput(): void {
    // clear input
    this.selectedFiles = this.isMultiple ? [] : null;
    //setTimeout(() => {
    //  (this.filesInput as any)._resetInputFile();
    //  (this.filesInput as any)._inputFileRef.nativeElement.value = "";
    //  (this.filesInput as any)._inputFileRef.nativeElement.value('');
    //});
  }
}

export interface FileUploaderItem<T> {
  id: number;
  fileName: string;
  fileSize: number;
  downloadUrl: string;

  data?: T;
}

export interface FileChangedEvent {
  /** negative for new files */
  id: number;
  fileName: string;
  fileSize: number;
  isRemoved: boolean;
  filePath: string;

  /** null if file is removed */
  uploadResult: UploadFileResult | null;
}

/** contains the file info internally inside the component */
interface FileItem {
  id: number;
  /** file id on server, null if not uploaded yet */
  serverId: string | null;
  fileName: string;
  fileSize: number;
  downloadUrl: string;
  isUploading: boolean;
  isWaiting: boolean;
  abortController: AbortController | null;
  uploadedPercentage: number;
  selectedFile: File | null;
}


export interface FileValidationError {
  fileName: string;
  fileSize: number;
  error: 'maxFileSize' | 'fileType';
  message: string;
}

export interface UploaderFileInfo {
  uid?: string;
  serverId?: string;
  name: string;
  extension?: string;
  size?: number;
  validationErrors?: string[];

  /**
   * The image src if the file is an image.
   */
  src?: string;
  /**
    * if file has not uploaded correctly
    */
  hasError?: boolean;
}

